import { createFileRoute } from "@tanstack/react-router";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { LEGAL_LAST_UPDATED } from "@/lib/legal";

export const Route = createFileRoute("/payment-protection-refunds")({
  head: () => ({
    meta: [
      { title: "Refund & Payment Protection Policy — Lumvio" },
      {
        name: "description",
        content:
          "Lumvio's refund, payment protection, milestone, dispute and crypto payout policy for clients and freelancers.",
      },
      { property: "og:title", content: "Refund & Payment Protection Policy — Lumvio" },
      {
        property: "og:description",
        content:
          "Lumvio's refund, payment protection, milestone, dispute and crypto payout policy for clients and freelancers.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://lumvio.co/payment-protection-refunds" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Refund & Payment Protection Policy — Lumvio" },
      {
        name: "twitter:description",
        content:
          "Lumvio's refund, payment protection, milestone, dispute and crypto payout policy for clients and freelancers.",
      },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/payment-protection-refunds" }],
  }),
  component: PaymentProtectionRefundsPage,
});

function PaymentProtectionRefundsPage() {
  return (
    <LegalPageLayout title="Refund & Payment Protection Policy" updated={LEGAL_LAST_UPDATED}>
      <p>
        This Refund &amp; Payment Protection Policy explains how funds are held, released, disputed, and refunded on
        Lumvio. It applies to all projects, milestones, and payouts processed through the platform and should be read
        together with our Terms of Service.
      </p>

      <h2>1. Payment protection</h2>
      <p>
        Lumvio holds client funds under payment protection for each milestone before work begins. The freelancer is
        paid only after the deliverable is submitted and the client explicitly approves it, or after the applicable
        auto-release window expires without objection. This is designed to reduce risk for both parties: the client
        knows the money is available, and the freelancer has a recorded release process on delivery.
      </p>
      <p>
        Protected funds remain Lumvio's responsibility until the milestone is marked complete or a dispute is resolved.
        Lumvio is not a bank and does not pay interest on protected balances.
      </p>

      <h2>2. Milestone Payments</h2>
      <p>
        Projects are divided into milestones agreed upon by the client and freelancer. Each milestone has a fixed
        amount that the client funds into payment protection before work starts. Once the client approves the milestone
        deliverable, the protected amount is released to the freelancer's Lumvio wallet, less any applicable platform
        or processing fees.
      </p>
      <p>
        <strong>Partial releases are non-refundable once approved.</strong> When a client approves a partial milestone
        release, they confirm that the delivered portion meets the agreed standard. Any later disagreement about that
        released portion must be handled through the dispute resolution process, but approval is treated as
        confirmation of satisfactory delivery.
      </p>

      <h2>3. Dispute Resolution</h2>
      <p>
        If a client or freelancer disagrees about whether a milestone was delivered satisfactorily, either party may
        open a dispute from the project workspace. The dispute must be raised within the time limits shown in the
        project timeline and before funds are auto-released.
      </p>
      <p>
        Both parties will be asked to submit evidence, including the project brief, deliverables, messages, files,
        revision history, and any other relevant context. Lumvio's platform admin will review the evidence objectively
        and issue a final decision.
      </p>
      <p>Lumvio may decide to:</p>
      <ul>
        <li>Release the full protected amount to the freelancer.</li>
        <li>Refund the full protected amount to the client.</li>
        <li>Split the protected amount as a partial refund to the client and partial release to the freelancer.</li>
        <li>Request additional information or a revised deliverable before releasing funds.</li>
      </ul>
      <p>
        Lumvio's decision on the release of protected funds is final and binding for the purpose of that release. It
        does not prevent either party from pursuing other legal remedies outside the platform.
      </p>

      <h2>4. Auto-Release Policy</h2>
      <p>
        Each project is configured with an auto-release window (for example, 24, 48, 72, or 168 hours) chosen when the
        project is created. When the freelancer marks a milestone as delivered, the client has until the end of that
        window to review the work and either approve it or open a dispute.
      </p>
      <p>
        If the client does not respond within the auto-release window, the milestone is considered accepted and the
        protected funds are automatically released to the freelancer. After auto-release, the funds are considered
        non-refundable except in cases of fraud, platform error, or a successful dispute filed before the window
        expired.
      </p>
      <p>
        Clients are encouraged to review deliverables promptly. Freelancers are encouraged to deliver clearly and on
        time so the client has enough time to review.
      </p>

      <h2>5. Crypto Payouts</h2>
      <p>
        Freelancers may withdraw approved earnings to a crypto wallet address they provide. Once a payout is approved
        by the freelancer (through the platform's OTP confirmation process) and processed by Lumvio, the transaction is
        broadcast to the blockchain.
      </p>
      <p>
        <strong>Crypto payouts are final and cannot be reversed.</strong> Because blockchain transactions are
        immutable, Lumvio cannot recall, cancel, or refund a payout after it has been sent. Freelancers are responsible
        for ensuring the wallet address is correct and belongs to them before confirming a payout.
      </p>
      <p>
        If a payout fails due to an invalid address or network error, Lumvio will attempt to return the funds to the
        freelancer's Lumvio wallet and notify the user to update the wallet address.
      </p>

      <h2>6. Contact</h2>
      <p>
        For questions about refunds, payment protection, or disputes, contact us at{" "}
        <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </LegalPageLayout>
  );
}
